import AppShell from "@/components/AppShell";
import DeleteConfirm from "@/components/DeleteConfirm";
import DetailsPanel from "@/components/DetailsPanel";
import { buildListOptions } from "@/lib/listOptions";
import EmptyState from "@/components/EmptyState";
import PageHeader from "@/components/PageHeader";
import SearchLink from "@/components/SearchLink";
import TaskContextMenu, { type MenuPatch } from "@/components/TaskContextMenu";
import TaskRow, { type TaskData } from "@/components/TaskRow";
import { plannedGroupLabel, todayIso } from "@/lib/dates";
import {
  toggleTask as mutateToggle,
  deleteTask as mutateDelete,
  updateTask,
  bulkPatchTasks,
  bulkDeleteTasks,
  reorderMyDayTask,
} from "@/mutations/tasks";
import { neighborsForReorder } from "@/lib/reorder";
import { useTaskSelection } from "@/hooks/useTaskSelection";
import { useLocalView } from "@/hooks/useLocalView";
import { isOfflineStandIn, OFFLINE_EMPTY_TEXT } from "@/lib/swClient";
import { planned as localPlanned } from "@/lib/views";
import { useLeavingRows } from "@/hooks/useLeavingRows";
import { isHiddenAt } from "@/lib/visibleFrom";
import { useNow } from "@/hooks/useTimeGate";
import { taskCount } from "@/lib/plural";
import type { SharedProps } from "@/types";
import { usePage } from "@inertiajs/react";
import { useDndMonitor, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useEffect, useMemo, useRef, useState } from "react";
import type { PressPoint } from "@/hooks/useLongPress";

interface PlannedGroup {
  key: "overdue" | "today" | "later_today" | "tomorrow" | "next_5_days" | "later";
  tasks: TaskData[];
}

interface Props {
  groups: PlannedGroup[];
  fetchedAt?: string;
}

const CALENDAR = (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const HEADER_TONE: Record<PlannedGroup["key"], string> = {
  overdue: "text-gray-500 dark:text-gray-400",
  today: "text-blue-500",
  later_today: "text-gray-500 dark:text-gray-400",
  tomorrow: "text-gray-500 dark:text-gray-400",
  next_5_days: "text-gray-500 dark:text-gray-400",
  later: "text-gray-500 dark:text-gray-400",
};

const ARRANGEABLE_GROUP: PlannedGroup["key"] = "tomorrow";

function arrangeableAhead(task: TaskData): boolean {
  return task.myDayOn !== todayIso();
}

function inArrangedOrder(tasks: TaskData[], arranged: string[] | null): TaskData[] {
  if (!arranged) return tasks;
  const rank = new Map(arranged.map((id, index) => [id, index]));
  return [...tasks].sort((a, b) => (rank.get(a.id) ?? Infinity) - (rank.get(b.id) ?? Infinity));
}

const GROUP_ORDER: PlannedGroup["key"][] = ["overdue", "today", "later_today", "tomorrow", "next_5_days", "later"];

function timeGatedGroup(key: PlannedGroup["key"], task: TaskData, now: Date): PlannedGroup["key"] | null {
  const waiting = isHiddenAt(task, now);
  if (key === "today") return waiting ? "later_today" : "today";
  if (key === "later_today") return waiting ? "later_today" : null;
  return key;
}

function groupByTimeGate(groups: PlannedGroup[], now: Date): PlannedGroup[] {
  const byKey = new Map<PlannedGroup["key"], TaskData[]>();
  for (const group of groups) {
    for (const task of group.tasks) {
      const key = timeGatedGroup(group.key, task, now);
      if (key) byKey.set(key, [...(byKey.get(key) ?? []), task]);
    }
  }
  return GROUP_ORDER.filter((key) => byKey.has(key)).map((key) => ({ key, tasks: byKey.get(key)! }));
}

export default function PlannedShow(props: Props) {
  const local = useLocalView("planned", localPlanned) as PlannedGroup[] | null;
  const groups = local ?? props.groups;
  const sidebar = usePage<SharedProps>().props.sidebar;
  const [selectedTask, setSelectedTask] = useState<TaskData | null>(null);
  const [taskPendingDelete, setTaskPendingDelete] = useState<TaskData | null>(null);
  const [bulkDeletePending, setBulkDeletePending] = useState<TaskData[] | null>(null);
  const [contextMenu, setContextMenu] = useState<{ tasks: TaskData[]; x: number; y: number } | null>(null);
  const [pendingArrangement, setPendingArrangement] = useState<string[] | null>(null);
  const latestArrangement = useRef(0);
  // The first non-empty group (in display order) is expanded by default; the rest start
  // collapsed — so the most urgent bucket that actually has tasks is open on arrival.
  const [openGroups, setOpenGroups] = useState<Set<PlannedGroup["key"]>>(() => {
    const first = groupByTimeGate(groups, new Date())[0]?.key;
    return new Set(first ? [first] : []);
  });

  function toggleGroup(key: PlannedGroup["key"]) {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function patchTaskFromMenu(task: TaskData, payload: MenuPatch) {
    updateTask(task.id, payload);
  }

  const listOptions = buildListOptions(sidebar);

  const allPropTasks = groups.flatMap((g) => g.tasks);

  const allVisibleTasks = useLeavingRows(allPropTasks);

  // Re-bucket visible tasks preserving each group's order. A leaving phantom from
  // useLeavingRows belongs to no group in the current `groups` prop (that's exactly why
  // it's a phantom — it's gone from the server data groups are built from), so this
  // lookup never finds it and it's silently dropped. The 200ms fade from Step 7 is
  // therefore inert on this page: forcing a dateless phantom into one of the date
  // buckets below would need inventing a group for it, which isn't worth the
  // complexity for a page where a row disappearing (its due date changed or task
  // completed elsewhere) is already a coarser-grained event than a single-list toggle.
  const now = useNow();
  const visibleById = new Map(allVisibleTasks.map((t) => [t.id, t]));
  const visibleGroups = groupByTimeGate(
    groups.map((group) => {
      const tasks = group.tasks.map((t) => visibleById.get(t.id)).filter((t): t is TaskData => t !== undefined);
      return { key: group.key, tasks: group.key === ARRANGEABLE_GROUP ? inArrangedOrder(tasks, pendingArrangement) : tasks };
    }),
    now,
  );

  // Selection axis = flat order of rows in open groups (collapsed rows aren't rendered).
  const selectableIds = useMemo(
    () =>
      visibleGroups
        .filter((g) => openGroups.has(g.key))
        .flatMap((g) => g.tasks.map((t) => t.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allVisibleTasks, openGroups, groups],
  );
  const taskById = useMemo(() => new Map(allVisibleTasks.map((t) => [t.id, t])), [allVisibleTasks]);
  const selection = useTaskSelection(selectableIds);

  function openContextMenu(point: PressPoint, task: TaskData) {
    const ids = selection.selectForContext(task.id);
    const picked = ids.map((id) => taskById.get(id)).filter((t): t is TaskData => !!t);
    setContextMenu({ tasks: picked.length ? picked : [task], x: point.clientX, y: point.clientY });
  }

  function selectRow(task: TaskData, e: React.MouseEvent) {
    const intent = selection.handleClick(task.id, e);
    setSelectedTask(intent === "open" ? task : null);
  }

  function applyMenuPatch(payload: MenuPatch) {
    if (!contextMenu) return;
    const ids = contextMenu.tasks.map((t) => t.id);
    if (ids.length === 1) {
      patchTaskFromMenu(contextMenu.tasks[0], payload);
    } else {
      bulkPatchTasks(ids, payload);
    }
    selection.clear();
  }

  function confirmBulkDelete() {
    if (!bulkDeletePending) return;
    bulkDeleteTasks(bulkDeletePending.map((t) => t.id));
    selection.clear();
    setBulkDeletePending(null);
  }

  useEffect(() => {
    if (!selectedTask) return;
    // Subtasks aren't always in the Planned prop set; let DetailsPanel manage them.
    if ((selectedTask.parentId ?? null) !== null) return;
    const fresh = allVisibleTasks.find((t) => t.id === selectedTask.id);
    if (!fresh) setSelectedTask(null);
    else if (fresh !== selectedTask) setSelectedTask(fresh);
  }, [allVisibleTasks, selectedTask]);

  useDndMonitor({
    onDragEnd: ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) return;
      const group = visibleGroups.find((g) => g.key === ARRANGEABLE_GROUP)?.tasks ?? [];
      const oldIndex = group.findIndex((t) => t.id === active.id);
      const newIndex = group.findIndex((t) => t.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const moved = group[oldIndex];
      if (!arrangeableAhead(moved)) return;
      const reordered = arrayMove(group, oldIndex, newIndex);
      setPendingArrangement(reordered.map((t) => t.id));
      const arrangement = ++latestArrangement.current;
      const settle = () => {
        if (arrangement === latestArrangement.current) setPendingArrangement(null);
      };
      reorderMyDayTask(
        moved.id,
        { ...neighborsForReorder(reordered.filter(arrangeableAhead), moved.id), date: moved.dueOn ?? undefined },
        { onSuccess: settle, onError: settle },
      );
    },
  });

  function renderRow(task: TaskData, draggable: boolean) {
    return (
      <TaskRow
        key={task.id}
        task={task}
        onToggle={toggleTask}
        onRequestDelete={setTaskPendingDelete}
        onDelete={deleteTaskNow}
        onSelect={selectRow}
        onContextMenu={openContextMenu}
        selected={selection.isSelected(task.id)}
        draggable={draggable && task.status === "pending" && !task.leaving && arrangeableAhead(task)}
        dragGroupIds={draggable ? selection.dragGroupFor(task.id) : undefined}
      />
    );
  }

  function toggleTask(task: TaskData) {
    mutateToggle(task);
  }

  function deleteTaskNow(task: TaskData) {
    mutateDelete(task.id);
  }

  function confirmDeleteTask() {
    if (!taskPendingDelete) return;
    deleteTaskNow(taskPendingDelete);
    setTaskPendingDelete(null);
  }

  return (
    <AppShell>
      <PageHeader title="Запланировано" actions={<SearchLink from="planned" />} />

      <div className="px-5 md:px-7">

        {visibleGroups.length === 0 ? (
          <EmptyState
            icon={CALENDAR}
            text={!local && isOfflineStandIn(props.fetchedAt) ? OFFLINE_EMPTY_TEXT : "Пока ничего не запланировано ✨ Задачи со сроком появятся здесь."}
          />
        ) : (
          visibleGroups.map((group) => {
            const open = openGroups.has(group.key);
            return (
              <section key={group.key} className="mt-6 first:mt-0">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.key)}
                  aria-expanded={open}
                  className={`flex items-center gap-1 text-xs font-medium uppercase tracking-wide mb-2 cursor-pointer ${HEADER_TONE[group.key]}`}
                >
                  <svg
                    className={`w-3 h-3 transition-transform ${open ? "rotate-90" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span>{plannedGroupLabel(group.key)}</span>
                  <span className="text-gray-400 dark:text-gray-500 tabular-nums normal-case tracking-normal">
                    {group.tasks.length}
                  </span>
                </button>
                {open && (
                  <div
                    className="-mx-2 md:-mx-4 space-y-1.5"
                    onClick={(e) => {
                      if (e.target === e.currentTarget) selection.clear();
                    }}
                  >
                    {group.key === ARRANGEABLE_GROUP ? (
                      <SortableContext items={group.tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                        {group.tasks.map((task) => renderRow(task, true))}
                      </SortableContext>
                    ) : (
                      group.tasks.map((task) => renderRow(task, false))
                    )}
                  </div>
                )}
              </section>
            );
          })
        )}
      </div>

      {taskPendingDelete && (
        <DeleteConfirm
          title={`Удалить «${taskPendingDelete.title}»?`}
          body="Задача будет удалена безвозвратно."
          onConfirm={confirmDeleteTask}
          onCancel={() => setTaskPendingDelete(null)}
        />
      )}

      {selectedTask && (
        <DetailsPanel
          task={selectedTask}
          lists={listOptions}
          onClose={() => setSelectedTask(null)}
          onDelete={(t) => {
            setSelectedTask(null);
            setTaskPendingDelete(t);
          }}
        />
      )}

      {bulkDeletePending && (
        <DeleteConfirm
          title={
            bulkDeletePending.length === 1
              ? `Удалить «${bulkDeletePending[0].title}»?`
              : `Удалить ${taskCount(bulkDeletePending.length)}?`
          }
          body={
            bulkDeletePending.length === 1
              ? "Задача будет удалена безвозвратно."
              : "Задачи будут удалены безвозвратно."
          }
          onConfirm={confirmBulkDelete}
          onCancel={() => setBulkDeletePending(null)}
        />
      )}

      {contextMenu && (
        <TaskContextMenu
          tasks={contextMenu.tasks}
          lists={listOptions}
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          onPatch={applyMenuPatch}
          onDelete={() => setBulkDeletePending(contextMenu.tasks)}
        />
      )}
    </AppShell>
  );
}
